import React, { useState, useEffect, useMemo } from 'react';
import { Button, Input, message, Table, InputNumber, AutoComplete, Select } from 'antd';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import IntlCurrencyInput from 'react-intl-currency-input';
import productService from '../../service/product.service';

const currencyConfig = {
    locale: 'pt-BR',
    formats: {
        number: {
            BRL: {
                style: 'currency',
                currency: 'BRL',
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            },
        },
    },
};

const weightConfig = {
    locale: 'pt-BR',
    formats: {
        number: {
            BRL: {
                style: 'decimal',
                minimumFractionDigits: 3,
                maximumFractionDigits: 3,
            },
        },
    },
};

const StyledButton = styled(Button)`
  margin-top: 16px;
  background-color: #012443;
  &:hover {
    background-color: #023d70ff !important;
  }
`;

export const PRICING_TYPE_TOTAL = "Total";
export const PRICING_TYPE_TON = "Ton";

export function parsePricingNumericValue(value) {
  if (value === null || value === undefined || value === "") return NaN;
  if (typeof value === "number" && Number.isFinite(value)) return value;

  const normalized = String(value).trim().replace(/\./g, "").replace(",", ".");
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : NaN;
}

export function isFreteProductRow(row = {}) {
  return String(row?.nameProducts || "").trim().toLowerCase() === "frete";
}

export function resolvePricingType(row = {}) {
  if (!isFreteProductRow(row)) return PRICING_TYPE_TOTAL;
  const type = String(row?.pricingType || PRICING_TYPE_TOTAL).trim();
  return type === PRICING_TYPE_TON ? PRICING_TYPE_TON : PRICING_TYPE_TOTAL;
}

export function calculatePricingRowTotal(row = {}) {
  const amount = parsePricingNumericValue(row.amount);
  const priceList = Number(row.priceList) || 0;
  const weight = Number.isFinite(amount) ? amount : 0;

  if (weight <= 0 || priceList <= 0) return 0;

  if (isFreteProductRow(row) && resolvePricingType(row) === PRICING_TYPE_TON) {
    return Number(((weight * priceList) / 1000).toFixed(2));
  }

  return Number((weight * priceList).toFixed(2));
}

export function calculatePriceListFromTotal(row = {}, targetTotal) {
  const amount = Number(parsePricingNumericValue(row.amount)) || 1;
  const total = Number(targetTotal) || 0;

  if (isFreteProductRow(row) && resolvePricingType(row) === PRICING_TYPE_TON) {
    return Number(((total * 1000) / amount).toFixed(2));
  }

  return Number((total / amount).toFixed(2));
}

export function normalizePricingProductRow(row = {}) {
  const amountValue = parsePricingNumericValue(row.amount);
  const amount = Number.isFinite(amountValue) ? amountValue : 0;
  const priceList = Number(row.priceList) || 0;
  const rowWithType = {
    ...row,
    pricingType: resolvePricingType(row),
  };
  const computedTotal = calculatePricingRowTotal(rowWithType);
  const explicitTotal = parsePricingNumericValue(row.total);
  const total =
    amount > 0 && priceList > 0
      ? computedTotal
      : Number.isFinite(explicitTotal) && explicitTotal >= 0
        ? explicitTotal
        : 0;

  return {
    ...rowWithType,
    total,
    netPrice: total,
  };
}

export function normalizePricingProducts(products = []) {
  if (!Array.isArray(products)) return [];
  return products.map(normalizePricingProductRow);
}

export function sumPricingProductsTotal(products = []) {
  return normalizePricingProducts(products).reduce(
    (sum, prod) => sum + (Number(prod.total) || 0),
    0,
  );
}

export function syncPricingProductsWithFreightValue(
  products = [],
  valueFreightage,
) {
  const freightValue = parsePricingNumericValue(valueFreightage);
  const normalized = normalizePricingProducts(products);

  if (!Number.isFinite(freightValue) || freightValue < 0) {
    return normalized;
  }

  const currentSum = sumPricingProductsTotal(normalized);
  if (Math.abs(currentSum - freightValue) < 0.005) {
    return normalized;
  }

  const hasContent = (row) =>
    String(row?.nameProducts || "").trim() !== "" ||
    Number(row?.amount) > 0 ||
    Number(row?.priceList) > 0 ||
    Number(row?.total) > 0;

  const filledRows = normalized.filter(hasContent);

  if (filledRows.length === 0) {
    return [
      {
        nameProducts: "Frete",
        pricingType: PRICING_TYPE_TOTAL,
        amount: 1,
        priceList: freightValue,
        total: freightValue,
        netPrice: freightValue,
      },
    ];
  }

  if (filledRows.length === 1) {
    const row = filledRows[0];
    const isTonFrete =
      isFreteProductRow(row) && resolvePricingType(row) === PRICING_TYPE_TON;
    const amount = isTonFrete
      ? Number(parsePricingNumericValue(row.amount)) || 1
      : 1;
    const priceList = isTonFrete
      ? calculatePriceListFromTotal({ ...row, amount }, freightValue)
      : freightValue;

    return normalizePricingProducts([
      {
        ...row,
        amount,
        priceList,
        total: freightValue,
        netPrice: freightValue,
      },
    ]);
  }

  const targetIdx = normalized.findIndex(hasContent);
  if (targetIdx === -1) return normalized;

  const othersSum = normalized.reduce(
    (sum, row, index) =>
      index === targetIdx ? sum : sum + (Number(row.total) || 0),
    0,
  );
  const targetTotal = Number((freightValue - othersSum).toFixed(2));
  const targetRow = normalized[targetIdx];
  const amount = Number(targetRow.amount) || 1;
  const priceList = calculatePriceListFromTotal(targetRow, targetTotal);

  return normalizePricingProducts(
    normalized.map((row, index) =>
      index === targetIdx
        ? {
            ...row,
            priceList,
            total: targetTotal,
            netPrice: targetTotal,
          }
        : row,
    ),
  );
}

export function extractSimulationFreightValue(simulationData = {}) {
  const freightData = simulationData?.freight || {};
  const candidates = [
    freightData?.valueFreightage,
    simulationData?.valueFreightage,
    freightData?.value,
    simulationData?.value,
    freightData?.total,
    simulationData?.total,
  ];

  for (const candidate of candidates) {
    const parsed = parsePricingNumericValue(candidate);
    if (Number.isFinite(parsed) && parsed >= 0) {
      return parsed;
    }
  }

  return undefined;
}

const ProductTable = ({
  product,
  onChange,
  disabled = false,
  unitPricePrecision = 2,
  generalTotalOverride,
}) => {

    const [products, setProducts] = useState([
        createProducts(),
    ]);
    const [productsData, setProductsData] = useState([]);

    const navigate = useNavigate();

    // Formatação para exibição (totais, etc.) sempre com 2 casas decimais
    const formatCurrency = (value) => {
        if (value === null || value === undefined || value === '') return '';

        let numberValue = typeof value === 'number' ? value : parseFloat(value);
        if (isNaN(numberValue)) return '';

        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(numberValue);
    };

    const roundUnitPrice = (value) => {
        if (value === '' || value === null || value === undefined) return value;
        const factor = 10 ** unitPricePrecision;
        return Math.round(Number(value) * factor) / factor;
    };

    const roundWeight = (value) => {
        if (value === '' || value === null || value === undefined) return value;
        return Math.round(Number(value) * 1000) / 1000;
    };

    const registeredProductNames = useMemo(
        () =>
            new Set(
                productsData
                    .map((prod) => String(prod?.product || '').trim())
                    .filter(Boolean),
            ),
        [productsData],
    );

    const productOptions = useMemo(
        () =>
            productsData.map((prod) => ({
                value: String(prod.product || '').trim(),
            })),
        [productsData],
    );

    function handleProductNameChange(index, value) {
        const trimmed = String(value || '').trim();
        if (trimmed && !registeredProductNames.has(trimmed)) {
            return;
        }
        handleChangeProducts(index, 'nameProducts', trimmed);
    }

    const getAmountLabel = (row) =>
        isFreteProductRow(row) && resolvePricingType(row) === PRICING_TYPE_TON
            ? 'Peso Estimado (Kg)'
            : 'Quantidade';

    const getPriceLabel = (row) =>
        isFreteProductRow(row) && resolvePricingType(row) === PRICING_TYPE_TON
            ? 'Valor por Ton'
            : 'Valor Unitário';

    const calculateTotals = (productRow) => {
        const amount = parsePricingNumericValue(productRow.amount);
        const pricingType = isFreteProductRow(productRow)
            ? resolvePricingType(productRow)
            : PRICING_TYPE_TOTAL;
        const total = calculatePricingRowTotal({
            ...productRow,
            pricingType,
        });

        return normalizePricingProductRow({
            ...productRow,
            pricingType,
            amount: Number.isFinite(amount) ? amount : productRow.amount,
            total,
        });
    };

    useEffect(() => {
        if (product && product.length > 0) {
            setProducts(normalizePricingProducts(product));
        }
    }, [product]);

    useEffect(() => {
        const fetchProducts = async () => {
            try {
                const snapshot = await productService.getProduct();
                setProductsData(snapshot);
            } catch (error) {
                message.error("Erro ao carregar os produtos. Tente novamente.");
                console.error(error);
            }
        };
        fetchProducts();
    }, []);

    function createProducts() {
        return {
            nameProducts: "",
            pricingType: PRICING_TYPE_TOTAL,
            amount: "",
            priceList: "",
            total: "",
            netPrice: "",
        };
    };

    function handleChangeProducts(index, field, value) {
        const updatedProducts = [...products];
        const row = products[index];
        const isTonWeight =
            isFreteProductRow(row) && resolvePricingType(row) === PRICING_TYPE_TON;
        const normalizedValue =
            field === 'priceList'
                ? roundUnitPrice(value)
                : field === 'amount'
                  ? value === '' || value === null || value === undefined
                    ? ''
                    : isTonWeight
                      ? roundWeight(value)
                      : parsePricingNumericValue(value)
                  : value;
        updatedProducts[index][field] = Number.isNaN(normalizedValue)
            ? value
            : normalizedValue;

        if (field === 'nameProducts') {
            const isFrete =
                String(normalizedValue || '').trim().toLowerCase() === 'frete';
            updatedProducts[index].pricingType = isFrete
                ? updatedProducts[index].pricingType || PRICING_TYPE_TOTAL
                : PRICING_TYPE_TOTAL;
        }

        if (
            field === 'amount' ||
            field === 'priceList' ||
            field === 'nameProducts' ||
            field === 'pricingType'
        ) {
            updatedProducts[index] = calculateTotals(updatedProducts[index]);
        }
        setProducts(updatedProducts);
        onChange && onChange(updatedProducts);
    };

    function handleAddProduct() {
        const newProducts = [...products, createProducts()];
        setProducts(newProducts);
        onChange && onChange(newProducts);
    };

    function handleRemoveProduct(index) {
        const updatedProducts = [...products];
        updatedProducts.splice(index, 1);
        setProducts(updatedProducts);
        onChange && onChange(updatedProducts);
    };

    const displayedGeneralTotal = (() => {
        const overrideValue = parsePricingNumericValue(generalTotalOverride);
        if (Number.isFinite(overrideValue)) {
            return overrideValue;
        }
        return sumPricingProductsTotal(products);
    })();

    return (
        <div>
            <Table pagination={false} dataSource={products} rowKey={(record, index) => index}>
                <Table.Column
                    title="Nome do Produto"
                    dataIndex="nameProducts"
                    key="nameProducts"
                    render={(text, record, index) => (
                        <AutoComplete
                        style={{ width: '100%' }}
                        options={productOptions}
                        value={record.nameProducts}
                        placeholder="Busque e selecione o produto"
                        disabled={disabled}
                        filterOption={(inputValue, option) =>
                            option.value.toUpperCase().includes(inputValue.toUpperCase())
                        }
                        onSelect={(value) => handleProductNameChange(index, value)}
                        onChange={(value) => handleProductNameChange(index, value)}
                        onBlur={() => {
                            if (
                                record.nameProducts &&
                                !registeredProductNames.has(
                                    String(record.nameProducts).trim(),
                                )
                            ) {
                                handleChangeProducts(index, 'nameProducts', '');
                            }
                        }}
                        >
                        <Input disabled={disabled} />
                        </AutoComplete>
                    )}
                />
                <Table.Column title="Tipo" key="pricingType"
                    render={(_, record, index) =>
                        isFreteProductRow(record) ? (
                            <Select
                                style={{ width: '100%' }}
                                value={resolvePricingType(record)}
                                disabled={disabled}
                                options={[
                                    { value: PRICING_TYPE_TOTAL, label: 'Total' },
                                    { value: PRICING_TYPE_TON, label: 'Ton' },
                                ]}
                                onChange={(value) =>
                                    handleChangeProducts(index, 'pricingType', value)
                                }
                            />
                        ) : null
                    }
                />
                <Table.Column title="Quantidade" dataIndex="amount" key="amount"
                    render={(text, record, index) => {
                        const isTonWeight =
                            isFreteProductRow(record) &&
                            resolvePricingType(record) === PRICING_TYPE_TON;

                        return (
                        <div>
                            {isTonWeight && (
                                    <div
                                        style={{
                                            fontSize: 12,
                                            color: '#666',
                                            marginBottom: 4,
                                        }}
                                    >
                                        {getAmountLabel(record)}
                                    </div>
                                )}
                            {isTonWeight ? (
                                <IntlCurrencyInput
                                    currency="BRL"
                                    config={weightConfig}
                                    className="form-control"
                                    value={record.amount ?? ''}
                                    disabled={disabled}
                                    style={{ width: '100%', borderRadius: '6px' }}
                                    onChange={(event, value) =>
                                        handleChangeProducts(index, 'amount', value)
                                    }
                                />
                            ) : (
                                <InputNumber
                                    min={0}
                                    precision={2}
                                    step={0.01}
                                    decimalSeparator=","
                                    value={record.amount}
                                    disabled={disabled}
                                    onChange={(value) =>
                                        handleChangeProducts(index, 'amount', value)
                                    }
                                />
                            )}
                        </div>
                        );
                    }}
                />
                <Table.Column title="Valor Unitário" dataIndex="priceList" key="priceList"
                    render={(text, record, index) => (
                        <div>
                            {isFreteProductRow(record) &&
                                resolvePricingType(record) === PRICING_TYPE_TON && (
                                    <div
                                        style={{
                                            fontSize: 12,
                                            color: '#666',
                                            marginBottom: 4,
                                        }}
                                    >
                                        {getPriceLabel(record)}
                                    </div>
                                )}
                            <IntlCurrencyInput
                                currency="BRL"
                                config={currencyConfig}
                                className="form-control"
                                value={record.priceList ?? ''}
                                disabled={disabled}
                                style={{ width: '100%', borderRadius: '6px' }}
                                onChange={(event, value) =>
                                    handleChangeProducts(index, 'priceList', value)
                                }
                            />
                        </div>
                    )}
                />
                <Table.Column title="Valor Total" dataIndex="total" key="total"
                    render={(text, record, index) => (
                        <span>{formatCurrency(record.total)}</span>
                    )}
                />
                <Table.Column title="Ações" key="actions"
                    render={(text, record, index) => (
                        <Button
                            type="danger"
                            onClick={() => handleRemoveProduct(index)}
                            disabled={disabled || products.length === 1}
                            style={{ color: "red" }}
                        >
                            <DeleteOutlinedIcon />
                        </Button>
                    )}
                />
            </Table>
            {!disabled && (
                <StyledButton type="primary" onClick={handleAddProduct}>
                    Adicionar Produto
                </StyledButton>
            )}
            <span style={{marginLeft: "62%", marginTop: '16px', fontWeight: 'bold' }}>
                Total Geral: {formatCurrency(displayedGeneralTotal)}
            </span>
        </div>
    );

};

export default ProductTable;